import {
    booleanToGateBoolean,
    gateBooleanToBoolean,
    gateNonstandardDateToDate,
} from "../../sg-core/utils/GateSupport";
import { base64ToJson, jsonToBase64 } from "../../sg-core/utils/json.helpers";
import { IAuditMeta, INewAudit } from "../interfaces/IAudit";
import { IAuditResult, ICreateAuditBody } from "../services/auditService";
import IAudit, { EAuditStatus, isAuditStatus } from "../interfaces/IAudit";
import { calculateEvaluationStatics } from "../utils/calculateEvaluationStatics";
import { resolveEvaluationsData } from "../utils/evaluation.helpers";

type TAuditBase64Data = Pick<IAudit, "formData" | "formSchema" | "notes" | "modifiedAt">;

const AuditMapper = {
    apiToDomain: (api: IAuditResult["auditointi"]): IAudit => {
        if (!api.lomakedata) {
            console.warn("[AuditMapper.apiToDomain]: Audit api result is missing lomakedata");
        }

        const decodedBase64 = api.lomakedata ? base64ToJson(api.lomakedata) : { formSchema: {}, notes: {} };
        const decodedMetaData = api.metadata ? base64ToJson(api.metadata) : undefined;

        const { formSchema, notes, formData, modifiedAt } = decodedBase64 as TAuditBase64Data;

        const metaData = decodedMetaData || decodedBase64.metaData;
        const statics = decodedMetaData?.statics || decodedBase64?.statics;

        if (!formSchema || !metaData) {
            console.warn("[AuditMapper.apiToDomain]: Audit is missing fromSchema or metaData");
        }

        const status = isAuditStatus(api.tila) ? api.tila : EAuditStatus.Unknown;
        const { otsikko: title, alue: target } = api;
        const mapLocation = api.karttasijainti ? api.karttasijainti : undefined;

        if (mapLocation && formData.karttasijainti?.marker) {
            formData.karttasijainti.marker = mapLocation;
        }

        return {
            id: api.id,
            guid: api.guid,
            title,
            target,
            status,

            mapLocation,
            formType: api.lomake,
            work: api.tyo,

            image: metaData.images && metaData.images[0] ? metaData.images[0].id : null,

            formData: {
                ...formData,
            },

            formSchema,

            metaData: metaData
                ? {
                      ...metaData,
                      responsiblePerson: api.vastuuhenkilo,
                      target,
                  }
                : {},

            notes: notes,
            statics: statics
                ? statics
                : formSchema
                ? calculateEvaluationStatics(resolveEvaluationsData(formData, formSchema) || {})
                : undefined,

            submitter: api.kirjaaja,
            submittedAt: api.kirjattu ? gateNonstandardDateToDate(api.kirjattu) || null : null,

            creator: api.creator,
            createdAt: api.created ? gateNonstandardDateToDate(api.created) || null : null,

            modifier: (api as any).modifier,
            modifiedAt: modifiedAt ? new Date(modifiedAt as any) : null,
            removed: gateBooleanToBoolean(api.poistettu),
        };
    },

    domainToApi: (domain: IAudit): IAuditResult["auditointi"] => {
        const valuationData = domain.formSchema
            ? resolveEvaluationsData(domain.formData, domain.formSchema)
            : undefined;

        const jsonData: TAuditBase64Data = {
            notes: domain.notes,
            formSchema: domain.formSchema,
            formData: domain.formData,
            modifiedAt:
                typeof domain.modifiedAt === "string"
                    ? domain.modifiedAt
                    : (domain.modifiedAt?.toISOString() as any),
        };

        const metaData: IAuditMeta = {
            ...domain.metaData,
            statics: valuationData ? calculateEvaluationStatics(valuationData) : undefined,
        };

        return {
            id: domain.id,
            guid: domain.guid,
            otsikko: domain.title,

            lomake: domain.formType,
            tyo: domain.work || 1,
            tila: domain.status,
            alue: domain.metaData.target || domain.target || undefined,

            vastuuhenkilo: domain.metaData.responsiblePerson,

            karttasijainti: domain.formData.karttasijainti?.marker || domain.mapLocation,

            kirjaaja: domain.submitter,
            kirjattu: domain.submittedAt?.toISOString(),

            created: domain.createdAt?.toISOString(),
            creator: domain.creator,

            modified:
                typeof domain.modifiedAt === "string" ? domain.modifiedAt : domain.modifiedAt?.toISOString(),
            modifier: domain.modifier,

            lomakedata: jsonToBase64(jsonData),
            metadata: jsonToBase64(metaData),

            lomakedatahaku: jsonToBase64(domain.__searchableText as any),

            poistettu: booleanToGateBoolean(domain.removed),
        };
    },
};

export default AuditMapper;

export const newAuditMapper = {
    domainToApi: (domain: INewAudit): ICreateAuditBody["auditointi"] => {
        const valuationData = domain.formSchema
            ? resolveEvaluationsData(domain.formData, domain.formSchema)
            : undefined;

        const jsonData: TAuditBase64Data = {
            notes: domain.notes,
            formSchema: domain.formSchema,
            formData: domain.formData,
            modifiedAt: new Date().toISOString() as any,
        };

        const metaData = {
            ...domain.metaData,
            statics: valuationData ? calculateEvaluationStatics(valuationData) : undefined,
        };

        return {
            otsikko: domain.title,

            lomake: domain.formType,
            tyo: domain.work || 1,
            tila: EAuditStatus.Draft,
            alue: domain.metaData.target || domain.target || undefined,
            karttasijainti: domain.formData.karttasijainti?.marker,

            vastuuhenkilo: domain.metaData.responsiblePerson,

            created: domain.createdAt?.toISOString(),
            creator: domain.creator,

            metadata: jsonToBase64(metaData),
            lomakedata: jsonToBase64(jsonData),
            lomakedatahaku: jsonToBase64(domain.__searchableText as any),
        };
    },
};
